import { messageDir } from './message-direction';

/**
 * Customer history on the contact screen — every rule the screen draws by, as pure functions whose only import is
 * the pure `messageDir` (so a unit test never drags the app's graph). The contract, the vectors and the copy are in
 * `native-apps/shared/customer-history/` (`contract.md`, `fixture.json`, `strings.json`); iOS
 * `ContactDetailScreen` and Android `ContactDetail.kt` are written against the same files. The read hook is
 * `lib/use-customer-history.ts`, the renderer `components/symphony/contacts/CustomerHistorySection.tsx`.
 */

// ---- The wire (contract v1) ------------------------------------------------------------------------------

export interface CustomerMoney {
  value?: string;
  currency?: string;
  formatted?: string;
}

export interface CustomerOrder {
  id?: string;
  number?: string;
  createdAt?: string;
  total?: CustomerMoney;
  payment?: string;
  fulfillment?: string;
}

export interface CustomerBooking {
  id?: string;
  serviceName?: string;
  start?: string;
  end?: string;
  status?: string;
}

export interface CustomerInvoice {
  id?: string;
  number?: string;
  issuedAt?: string;
  dueAt?: string | null;
  total?: CustomerMoney;
  status?: string;
}

export interface CustomerMessage {
  fromCustomer?: boolean;
  text?: string;
  at?: string;
}

export interface CustomerRecord {
  siteId?: string;
  siteName?: string;
  contactId?: string;
  name?: string;
  firstSeenAt?: string | null;
  lastSeenAt?: string | null;
  totalSpent?: { status?: string; amount?: CustomerMoney | null };
  orders?: { status?: string; count?: number; items?: CustomerOrder[] };
  bookings?: { status?: string; upcoming?: CustomerBooking[]; past?: CustomerBooking[] };
  invoices?: { status?: string; items?: CustomerInvoice[] };
  inbox?: { status?: string; conversationId?: string; lastMessageAt?: string | null; messages?: CustomerMessage[] };
}

export interface CustomerHistoryAnswer {
  contractVersion?: number;
  enabled?: boolean;
  sitesUnreadable?: number;
  customers?: CustomerRecord[];
}

/**
 * What the read came back as. A non-200 is NOT thrown: it is an answer the screen draws (`failed`), except a
 * 404 — the route not deployed yet — which draws nothing (`notDeployed`).
 */
export type CustomerHistoryRead = { status: 'ok'; answer: CustomerHistoryAnswer } | { status: 'failed' } | { status: 'notDeployed' };

/** The read's outcome from an HTTP status and its parsed body (null when the body did not parse). */
export function customerHistoryReadFrom(httpStatus: number, body: unknown): CustomerHistoryRead {
  if (httpStatus === 404) return { status: 'notDeployed' };
  if (httpStatus !== 200 || !body || typeof body !== 'object') return { status: 'failed' };
  return { status: 'ok', answer: body as CustomerHistoryAnswer };
}

// ---- The lookup keys ---------------------------------------------------------------------------------------

export const MAX_LOOKUP_KEYS = 5;
export const MAX_LOOKUP_KEY_LENGTH = 254;

/** Which lookup list a channel's value goes in: its `kind` decides; an unknown kind is classified by its value. */
function keyKind(kind: string | undefined, value: string): 'email' | 'phone' | null {
  switch ((kind ?? '').trim().toLowerCase()) {
    case 'email':
      return 'email';
    case 'phone':
    case 'sms':
    case 'whatsapp':
      return 'phone';
    default: {
      if (value.includes('@')) return 'email';
      const digits = value.replace(/\D/g, '').length;
      return /^[\d\s+\-().]+$/.test(value) && digits >= 7 ? 'phone' : null;
    }
  }
}

/**
 * The contact's own addresses as the read's lookup keys — the same rule as iOS `CustomerHistory.lookupKeys` and
 * Android's twin: a channel's `kind` decides (`email`; `phone` / `sms` / `whatsapp`), an unknown kind is
 * classified by its value (an `@` → email, a phone-shaped value with 7+ digits → phone). Values are sent as the
 * screen holds them (trimmed), each once (emails case-insensitively), at most 5 of each in channel order and at
 * most 254 characters. Sorted afterwards, so the query key is the same for the same person.
 */
export function customerLookupKeys(channels: ReadonlyArray<{ kind?: string; value?: string }> | undefined): { emails: string[]; phones: string[] } {
  const emails: string[] = [];
  const phones: string[] = [];
  for (const channel of channels ?? []) {
    const value = (channel.value ?? '').trim();
    if (!value || value.length > MAX_LOOKUP_KEY_LENGTH) continue;
    const kind = keyKind(channel.kind, value);
    if (kind === 'email') {
      if (emails.length < MAX_LOOKUP_KEYS && !emails.some((e) => e.toLowerCase() === value.toLowerCase())) emails.push(value);
    } else if (kind === 'phone') {
      if (phones.length < MAX_LOOKUP_KEYS && !phones.includes(value)) phones.push(value);
    }
  }
  return { emails: emails.sort(), phones: phones.sort() };
}

// ---- Chips -------------------------------------------------------------------------------------------------

/** A chip's tone — the renderer maps it onto its own badge style. The same four tones and table as iOS/Android. */
export type ChipTone = 'positive' | 'attention' | 'danger' | 'neutral';

export interface CustomerChip {
  key: string;
  tone: ChipTone;
}

const PAYMENT_TONES: Record<string, ChipTone> = {
  paid: 'positive',
  unpaid: 'attention',
  partiallyPaid: 'attention',
  refunded: 'neutral',
  partiallyRefunded: 'neutral',
  pending: 'attention',
};
const FULFILLMENT_TONES: Record<string, ChipTone> = {
  fulfilled: 'positive',
  unfulfilled: 'attention',
  partial: 'attention',
};
const INVOICE_TONES: Record<string, ChipTone> = {
  paid: 'positive',
  unpaid: 'attention',
  overdue: 'danger',
  draft: 'neutral',
  void: 'neutral',
};
// Only pending and canceled draw a chip on a booking (contract §Layout 4): a confirmed booking is the normal case.
const BOOKING_TONES: Record<string, ChipTone> = {
  pending: 'attention',
  canceled: 'neutral',
};

function chipFrom(table: Record<string, ChipTone>, prefix: string, token: string | undefined): CustomerChip | null {
  if (!token || !Object.prototype.hasOwnProperty.call(table, token)) return null;
  return { key: `contacts.customer.${prefix}.${token}`, tone: table[token] };
}

/** The payment chip; `unknown` (and any token this build does not know) draws none. */
export const paymentChip = (token: string | undefined) => chipFrom(PAYMENT_TONES, 'payment', token);
/** The fulfilment chip; `none` (nothing to ship) and `unknown` draw none. */
export const fulfillmentChip = (token: string | undefined) => chipFrom(FULFILLMENT_TONES, 'fulfillment', token);
/** The invoice status chip; `unknown` draws none. */
export const invoiceChip = (token: string | undefined) => chipFrom(INVOICE_TONES, 'invoice', token);
/** The booking chip: pending and canceled only. */
export const bookingChip = (token: string | undefined) => chipFrom(BOOKING_TONES, 'booking', token);

// ---- Links -------------------------------------------------------------------------------------------------

/** A new chat seeded with the owner's own prompt, in the app's language (URL-encoded once). */
export function newChatLink(prompt: string): string {
  return `orion://time/chat/new?message=${encodeURIComponent(prompt)}`;
}

/**
 * The calendar day (`YYYY-MM-DD`) an instant falls on in a time zone — the device's when none is given. Null
 * for an unreadable instant.
 */
export function localDay(iso: string | undefined | null, timeZone?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  // en-CA formats a date as YYYY-MM-DD.
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** The Calendar on the day a booking starts (device time zone), or null when the start is unreadable. */
export function calendarDayLink(start: string | undefined, timeZone?: string): string | null {
  const day = localDay(start, timeZone);
  return day ? `orion://time/calendar/view?view=day&day=${day}` : null;
}

// ---- Dates as the screen prints them ------------------------------------------------------------------------

function validDate(iso: string | undefined | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** A date in the reader's language ("Oct 28, 2026"); '' when unreadable. */
export function formatCustomerDate(iso: string | undefined | null, locale: string, timeZone?: string): string {
  const date = validDate(iso);
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeZone }).format(date);
  }
}

/** A date and time in the reader's language ("Nov 6, 2026, 11:00 AM"); '' when unreadable. */
export function formatCustomerDateTime(iso: string | undefined | null, locale: string, timeZone?: string): string {
  const date = validDate(iso);
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(date);
  }
}

// ---- The screen model ----------------------------------------------------------------------------------------

export type Translate = (key: string, params?: Record<string, string | number>) => string;

export type SummaryValue = { kind: 'money'; formatted: string } | { kind: 'date'; at: string } | { kind: 'unreadable' };

export interface SummaryRow {
  labelKey: string;
  value: SummaryValue;
}

export interface OrderRowView {
  id: string;
  number: string;
  at?: string;
  total?: string;
  chips: CustomerChip[];
  link: string;
}

export interface BookingRowView {
  id: string;
  serviceName: string;
  start?: string;
  chip: CustomerChip | null;
  link: string | null;
}

export interface InvoiceRowView {
  id: string;
  number: string;
  at?: string;
  total?: string;
  chip: CustomerChip | null;
  link: string;
}

export interface MessageView {
  fromCustomer: boolean;
  /** The customer's messages under the contact screen's own name for them, the business's under "You". */
  speaker: string;
  text: string;
  /** The message's own direction, by majority script (Golden Rule 13), as iOS and Android lay it out. */
  dir: 'rtl' | 'ltr';
  at?: string;
}

/** A section that failed is `unreadable`, never `none`; an ok section with nothing in it is `none`. */
export type SectionView<T> = { kind: 'unreadable' } | { kind: 'none' } | ({ kind: 'items' } & T);

export interface CustomerView {
  key: string;
  title: { key: string; params: Record<string, string> };
  summary: SummaryRow[];
  orders: SectionView<{ rows: OrderRowView[]; totalCount: number | null }>;
  bookings: SectionView<{ upcoming: BookingRowView[]; past: BookingRowView[] }>;
  invoices: SectionView<{ rows: InvoiceRowView[] }>;
  inbox: SectionView<{ messages: MessageView[]; lastMessageAt?: string; replyLink: string }>;
}

/**
 * What the contact screen draws for the read:
 * - `nothing` — loading, no lookup keys, 404, `enabled: false`, or nobody's customer on any site read;
 * - `sitesUnreadable` — the `titleNoSite` heading over the one `sitesUnreadable` line (a non-200, or no
 *   customer while some site's lookup failed: a failed lookup is never drawn as "not a customer");
 * - `customers` — a section per customer, in the answer's order.
 */
export type CustomerHistoryView = { kind: 'nothing' } | { kind: 'sitesUnreadable' } | { kind: 'customers'; customers: CustomerView[] };

const ok = (section: { status?: string } | undefined) => section?.status === 'ok';

/**
 * The `{site}` a prompt names: the site's name, or its id when Wix sent no name (the agent resolves a site by id), so a
 * prompt never reads "on ." — the same rule as iOS `CustomerHistory.promptSite` and Android's twin.
 */
export function promptSite(customer: CustomerRecord): string {
  return customer.siteName || customer.siteId || '';
}

/** The heading: "Customer at {site}", or `titleNoSite` when the site has no name (never "Customer at "). */
export function customerTitle(customer: CustomerRecord): CustomerView['title'] {
  return customer.siteName ? { key: 'contacts.customer.title', params: { site: customer.siteName } } : { key: 'contacts.customer.titleNoSite', params: {} };
}

function customerView(customer: CustomerRecord, index: number, t: Translate, timeZone: string | undefined, contactName: string): CustomerView {
  const site = promptSite(customer);
  // The prompts and the Inbox speaker name the person by the contact screen's own (owner-side) name, NEVER the Wix
  // contact's `name`: that is customer-controlled text, and the prompt is sent to the agent as the owner's own words.
  const name = contactName;

  const summary: SummaryRow[] = [];
  if (customer.totalSpent?.status === 'failed') {
    summary.push({ labelKey: 'contacts.customer.totalSpent', value: { kind: 'unreadable' } });
  } else if (ok(customer.totalSpent) && customer.totalSpent?.amount?.formatted) {
    // `amount: null` (no paid order) leaves the row out; a zero is never drawn.
    summary.push({ labelKey: 'contacts.customer.totalSpent', value: { kind: 'money', formatted: customer.totalSpent.amount.formatted } });
  }
  if (customer.firstSeenAt) summary.push({ labelKey: 'contacts.customer.firstSeen', value: { kind: 'date', at: customer.firstSeenAt } });
  if (customer.lastSeenAt) summary.push({ labelKey: 'contacts.customer.lastSeen', value: { kind: 'date', at: customer.lastSeenAt } });

  const orders: CustomerView['orders'] = (() => {
    if (!ok(customer.orders)) return { kind: 'unreadable' };
    const items = customer.orders?.items ?? [];
    if (!items.length) return { kind: 'none' };
    const count = customer.orders?.count;
    return {
      kind: 'items',
      rows: items.map((order, i) => ({
        id: order.id || `order-${i}`,
        number: order.number ?? '',
        at: order.createdAt,
        total: order.total?.formatted,
        chips: [paymentChip(order.payment), fulfillmentChip(order.fulfillment)].filter((chip): chip is CustomerChip => !!chip),
        link: newChatLink(t('contacts.customer.prompt.order', { number: order.number ?? '', name, site })),
      })),
      totalCount: typeof count === 'number' && count > items.length ? count : null,
    };
  })();

  const bookingRow = (booking: CustomerBooking, i: number, group: string): BookingRowView => ({
    id: booking.id || `${group}-${i}`,
    serviceName: booking.serviceName ?? '',
    start: booking.start,
    chip: bookingChip(booking.status),
    link: calendarDayLink(booking.start, timeZone),
  });
  const bookings: CustomerView['bookings'] = (() => {
    if (!ok(customer.bookings)) return { kind: 'unreadable' };
    const upcoming = customer.bookings?.upcoming ?? [];
    const past = customer.bookings?.past ?? [];
    if (!upcoming.length && !past.length) return { kind: 'none' };
    return { kind: 'items', upcoming: upcoming.map((b, i) => bookingRow(b, i, 'upcoming')), past: past.map((b, i) => bookingRow(b, i, 'past')) };
  })();

  const invoices: CustomerView['invoices'] = (() => {
    if (!ok(customer.invoices)) return { kind: 'unreadable' };
    const items = customer.invoices?.items ?? [];
    if (!items.length) return { kind: 'none' };
    return {
      kind: 'items',
      rows: items.map((invoice, i) => ({
        id: invoice.id || `invoice-${i}`,
        number: invoice.number ?? '',
        at: invoice.issuedAt,
        total: invoice.total?.formatted,
        chip: invoiceChip(invoice.status),
        link: newChatLink(t('contacts.customer.prompt.invoice', { number: invoice.number ?? '', name, site })),
      })),
    };
  })();

  const inbox: CustomerView['inbox'] = (() => {
    if (!ok(customer.inbox)) return { kind: 'unreadable' };
    const messages = customer.inbox?.messages ?? [];
    if (!messages.length) return { kind: 'none' };
    return {
      kind: 'items',
      messages: messages.map((message) => {
        const text = message.text ?? '';
        const fromCustomer = !!message.fromCustomer;
        return { fromCustomer, speaker: fromCustomer ? name : t('contacts.customer.inbox.you'), text, dir: messageDir(text), at: message.at };
      }),
      // Messages are oldest first: with no `lastMessageAt`, the newest message's time, as on iOS and Android.
      lastMessageAt: customer.inbox?.lastMessageAt || messages[messages.length - 1]?.at || undefined,
      replyLink: newChatLink(t('contacts.customer.prompt.inbox', { name, site })),
    };
  })();

  return {
    key: `${customer.siteId ?? ''}:${customer.contactId ?? ''}:${index}`,
    title: customerTitle(customer),
    summary,
    orders,
    bookings,
    invoices,
    inbox,
  };
}

/**
 * `contactName` is the contact screen's own display name for this person (`ContactDetailBody`'s `displayName`): the
 * prompts and the Inbox speaker use it always, never the Wix contact's customer-controlled `name`.
 */
export function customerHistoryView(read: CustomerHistoryRead | undefined, t: Translate, timeZone?: string, contactName = ''): CustomerHistoryView {
  if (!read || read.status === 'notDeployed') return { kind: 'nothing' };
  if (read.status === 'failed') return { kind: 'sitesUnreadable' };
  const answer = read.answer;
  if (answer.enabled !== true) return { kind: 'nothing' };
  const customers = answer.customers ?? [];
  if (!customers.length) return (answer.sitesUnreadable ?? 0) > 0 ? { kind: 'sitesUnreadable' } : { kind: 'nothing' };
  return { kind: 'customers', customers: customers.map((customer, i) => customerView(customer, i, t, timeZone, contactName)) };
}
